import { produce } from 'immer';
import type { StoreApi } from 'zustand';
import { JOB_STATUS } from '@/lib/db-constants';
import type { Job } from '@/lib/db-row-types';
import { isDiligenceExportJob } from '@/utils/models/diligence-exports';
import type { JobWithDetails } from '@/utils/models/jobs';
import { fetchJobWithDetails, toJobWithDetailsFallback } from '@/utils/models/jobs';

export type JobsState = {
  jobs: JobWithDetails[];
  jobsLoading: boolean;
  isJobsMenuOpen: boolean;
  // Completed/failed diligence PDF exports, kept separate from the active-jobs
  // list above: the menu shows them for 48h and the site report modal for 30
  // days, instead of the active list's transient "gone after 10s" handling.
  diligenceExports: JobWithDetails[];
  setJobs: (jobs: JobWithDetails[]) => void;
  setJobsLoading: (loading: boolean) => void;
  clearJobs: () => void;
  setJob: (job: JobWithDetails) => void;
  updateJob: (job: Job) => void;
  removeJob: (id: string) => void;
  handleJobInsert: (workspaceId: string, job: Job) => Promise<void>;
  handleJobUpdate: (workspaceId: string, job: Job) => Promise<void>;
  getJobsByMapId: (mapId: string | undefined) => JobWithDetails[];
  getJobsByLayerId: (layerId: string | undefined) => JobWithDetails[];
  hasActiveJobForLayer: (layerId: string | undefined) => boolean;
  setIsJobsMenuOpen: (isOpen: boolean) => void;
  setDiligenceExports: (exports: JobWithDetails[]) => void;
  upsertDiligenceExport: (job: JobWithDetails) => void;
  removeDiligenceExport: (id: string) => void;
};

/**
 * A realtime UPDATE for `jobs` can arrive with `payload` missing or stale —
 * Postgres logical replication omits an unchanged TOASTed column's value
 * unless the table has REPLICA IDENTITY FULL, and `complete_job` never
 * touches `payload`. If the job id is already known (e.g. from the
 * optimistic insert on kickoff, or a prior insert/update) to be a diligence
 * export, carry its payload forward so classification and site lookups
 * don't silently break on a payload-less row.
 */
function withKnownExportPayload(state: JobsState, job: Job): Job {
  if (isDiligenceExportJob(job)) return job;
  const known =
    state.jobs.find(j => j.id === job.id) ?? state.diligenceExports.find(j => j.id === job.id);
  return known && isDiligenceExportJob(known) ? { ...job, payload: known.payload } : job;
}

export const createJobsSlice = (
  set: StoreApi<any>['setState'],
  get: StoreApi<any>['getState']
) => ({
  jobs: [],
  jobsLoading: false,
  isJobsMenuOpen: false,
  diligenceExports: [],
  setJobs: (jobs: JobWithDetails[]) =>
    set(
      produce((draft: JobsState) => {
        draft.jobs = jobs;
        draft.jobsLoading = false;
      })
    ),
  setJobsLoading: (loading: boolean) =>
    set(
      produce((draft: JobsState) => {
        draft.jobsLoading = loading;
      })
    ),
  clearJobs: () =>
    set(
      produce((draft: JobsState) => {
        draft.jobs = [];
        draft.jobsLoading = false;
        draft.diligenceExports = [];
      })
    ),
  setJob: (job: JobWithDetails) =>
    set(
      produce((draft: JobsState) => {
        const index = draft.jobs.findIndex(j => j.id === job.id);
        if (index !== -1) {
          draft.jobs[index] = job;
        } else {
          draft.jobs.unshift(job);
        }
      })
    ),
  updateJob: (job: Job) =>
    set(
      produce((draft: JobsState) => {
        const index = draft.jobs.findIndex(j => j.id === job.id);
        if (index !== -1) {
          draft.jobs[index] = { ...draft.jobs[index], ...job };
        }
      })
    ),
  removeJob: (id: string) =>
    set(
      produce((draft: JobsState) => {
        draft.jobs = draft.jobs.filter(j => j.id !== id);
      })
    ),
  setDiligenceExports: (exports: JobWithDetails[]) =>
    set(
      produce((draft: JobsState) => {
        draft.diligenceExports = exports;
      })
    ),
  upsertDiligenceExport: (job: JobWithDetails) =>
    set(
      produce((draft: JobsState) => {
        const index = draft.diligenceExports.findIndex(j => j.id === job.id);
        if (index !== -1) {
          draft.diligenceExports[index] = job;
        } else {
          draft.diligenceExports.unshift(job);
        }
      })
    ),
  removeDiligenceExport: (id: string) =>
    set(
      produce((draft: JobsState) => {
        draft.diligenceExports = draft.diligenceExports.filter(j => j.id !== id);
      })
    ),
  handleJobInsert: async (workspaceId: string, rawJob: Job) => {
    if (rawJob.workspace_id !== workspaceId) return;
    // Layer-scoped jobs render inline on the layer row's spinner. Excluding
    // them from the global jobs slice keeps the workspace-level "Background
    // jobs" menu free of duplicates (GEO-700).
    if (rawJob.layer_id) return;
    // Export fragment children are internal fan-out units of their parent
    // export job — only the parent belongs in the jobs slice.
    if (rawJob.parent_job_id) return;

    const state = get() as JobsState;
    const job = withKnownExportPayload(state, rawJob);

    // A diligence export that's already terminal on insert (e.g. a fast
    // retry) goes straight to the export surface, same as the update path.
    if (
      isDiligenceExportJob(job) &&
      (job.status === JOB_STATUS.COMPLETED || job.status === JOB_STATUS.FAILED)
    ) {
      const { data: jobWithDetails } = await fetchJobWithDetails(job.id);
      // A read-after-write race on the join (e.g. a fresh INSERT) must not
      // silently drop the job — fall back to the raw realtime row.
      state.upsertDiligenceExport(jobWithDetails ?? toJobWithDetailsFallback(job));
      return;
    }

    if (
      job.status === JOB_STATUS.UPLOADING ||
      job.status === JOB_STATUS.PROCESSING ||
      job.status === JOB_STATUS.FAILED
    ) {
      const { data: jobWithDetails } = await fetchJobWithDetails(job.id);
      state.setJob(jobWithDetails ?? toJobWithDetailsFallback(job));
    }
  },
  handleJobUpdate: async (workspaceId: string, rawJob: Job) => {
    if (rawJob.workspace_id !== workspaceId) return;
    if (rawJob.layer_id) return;
    if (rawJob.parent_job_id) return;

    const state = get() as JobsState;
    const job = withKnownExportPayload(state, rawJob);

    // Diligence exports move to the dedicated export surface (48h in the jobs
    // menu, 30 days in the site report modal) instead of the active-jobs
    // list's transient completed-job handling below.
    if (
      isDiligenceExportJob(job) &&
      (job.status === JOB_STATUS.COMPLETED || job.status === JOB_STATUS.FAILED)
    ) {
      state.removeJob(job.id);
      const { data: jobWithDetails } = await fetchJobWithDetails(job.id);
      state.upsertDiligenceExport(jobWithDetails ?? toJobWithDetailsFallback(job));
      return;
    }

    const existingJob = state.jobs.find(j => j.id === job.id);

    if (
      job.status === JOB_STATUS.UPLOADING ||
      job.status === JOB_STATUS.PROCESSING ||
      job.status === JOB_STATUS.FAILED
    ) {
      if (existingJob) {
        state.updateJob(job);
      } else {
        const { data: jobWithDetails } = await fetchJobWithDetails(job.id);
        state.setJob(jobWithDetails ?? toJobWithDetailsFallback(job));
      }
    } else if (job.status === JOB_STATUS.COMPLETED) {
      state.updateJob(job);
      setTimeout(() => {
        const currentState = get() as JobsState;
        currentState.removeJob(job.id);
      }, 10000);
    } else {
      state.removeJob(job.id);
    }
  },
  getJobsByMapId: (mapId: string | undefined) => {
    if (!mapId) return [];
    const state = get() as JobsState;
    return state.jobs.filter(
      job =>
        job.map_id === mapId &&
        (job.status === JOB_STATUS.UPLOADING ||
          job.status === JOB_STATUS.PROCESSING ||
          job.status === JOB_STATUS.FAILED)
    );
  },
  getJobsByLayerId: (layerId: string | undefined) => {
    if (!layerId) return [];
    const state = get() as JobsState;
    return state.jobs.filter(
      job =>
        job.layer_id === layerId &&
        (job.status === JOB_STATUS.UPLOADING ||
          job.status === JOB_STATUS.PROCESSING ||
          job.status === JOB_STATUS.FAILED)
    );
  },
  hasActiveJobForLayer: (layerId: string | undefined) => {
    if (!layerId) return false;
    const state = get() as JobsState;
    return state.jobs.some(
      job =>
        job.layer_id === layerId &&
        (job.status === JOB_STATUS.UPLOADING || job.status === JOB_STATUS.PROCESSING)
    );
  },
  setIsJobsMenuOpen: (isOpen: boolean) =>
    set(
      produce((draft: JobsState) => {
        draft.isJobsMenuOpen = isOpen;
      })
    ),
});
